import { Button, Grid, LinearProgress, Typography } from '@material-ui/core';
import React, { useState } from 'react';
import BackButton from '../../../../utils/backButton';
import { HelpComponent } from '../../../../utils/helpComponent';
import { useStyles } from '../../../styles';
import SLATable from './components/SLATable';
import SLATransactionsTable from './components/SLATransactionsTable';
import SLAPopup from './components/SLAPopup';
import Add from '@material-ui/icons/Add';
import { useSLA } from './hooks/useSLA';
import { useParams } from 'react-router';
import EmptyConfigBox from './components/EmptyConfigBox';
import HELP_URL from '../../../../utils/helpLinkConstant';

const SLAPage = ({ isToolAdmin }: { isToolAdmin: boolean }) => {
  const classes = useStyles();
  const { projectId } = useParams();
  const emptyPopupState = {
    open: false,
    title: '',
    type: '',
    onClose: () => {},
    onConfirm: (data?: any, id?: any) => {},
    data: '',
  };

  const { slaList, isLoading, refreshSLAList, addSLA, addEditTransaction } =
    useSLA(projectId!);
  const [popupState, setPopupState] = useState(emptyPopupState);

  const allTransactions =
    slaList?.slas?.flatMap(
      (sla: any) => sla?.transactions || sla?.transactionDetails || [],
    ) || [];

  const slaIds = slaList?.slas?.map((sla: any) => sla?.slaId) || [];

  const handleSLA = (popupType: string, slaTransaction?: any) => {
    setPopupState({
      open: true,
      title: `${
        popupType === 'addSLA'
          ? 'Add SLA information'
          : popupType === 'editSLA'
          ? 'Edit SLA information'
          : popupType === 'addTransaction'
          ? 'Add transaction details'
          : 'Update transaction details'
      }`,
      type: popupType,
      onClose: () => setPopupState(emptyPopupState),
      onConfirm: (data, id) => {
        switch (popupType) {
          case 'addSLA':
            addSLA(data).then(() => refreshSLAList());
            break;
          case 'editSLA':
            addSLA(data, id).then(() => refreshSLAList());
            break;
          case 'addTransaction':
            addEditTransaction(data, id).then(() => refreshSLAList());
            break;
          case 'editTransaction':
            addEditTransaction(data, id, 'edit').then(() => refreshSLAList());
            break;
          default:
            break;
        }
        setPopupState(emptyPopupState);
      },
      data:
        popupType === 'addTransaction'
          ? slaIds
          : popupType === 'editSLA' || popupType === 'editTransaction'
          ? {
              slaIds,
              availableUserBaseLocations: slaList?.availableUserBaseLocations,
              ...slaTransaction,
            }
          : popupType === 'addSLA'
          ? slaList?.availableUserBaseLocations
          : '',
    });
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          className={classes.flexSpacebetween}
        >
          <BackButton />
          <HelpComponent helpUrl={HELP_URL.PERFORMANCE_TEST_SLA_PAGE} />
        </Grid>
      </Grid>
      {isLoading ? (
        <LinearProgress />
      ) : (
        <>
          <Grid container spacing={2} className={classes.pageContainerPadding}>
            <Grid item xs={12} sm={12} md={9} lg={9}>
              <Typography variant="h5">Application Level SLA's</Typography>
            </Grid>
            {slaList?.slas !== null && !slaList?.slas?.length > 0 && (
              <Grid
                item
                xs={12}
                sm={12}
                md={3}
                lg={3}
                className={classes.flexEnd}
              >
                <Button
                  className={classes.slaButton}
                  variant="contained"
                  onClick={() => handleSLA('addSLA')}
                >
                  <Add fontSize="small" />
                  <span className={classes.marginLeftSmall}>Add</span>
                </Button>
              </Grid>
            )}
          </Grid>
          {slaList?.slas !== null ? (
            <>
              <Grid
                container
                spacing={0}
                className={classes.pageContainerPadding}
              >
                <Grid item xs={12} sm={12}>
                  <SLATable onEdit={handleSLA} slaList={slaList?.slas} />
                </Grid>
              </Grid>
              <Grid
                container
                spacing={2}
                className={classes.pageContainerPadding}
              >
                <Grid item xs={12} sm={12} md={9} lg={9}>
                  <Typography variant="h6">Transaction details</Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={3}
                  lg={3}
                  className={classes.flexEnd}
                >
                  <Button
                    className={classes.slaButton}
                    variant="contained"
                    onClick={() => {
                      handleSLA('addTransaction');
                    }}
                  >
                    <Add fontSize="small" />
                    <span className={classes.marginLeftSmall}>Add</span>
                  </Button>
                </Grid>
              </Grid>
              {allTransactions?.length > 0 && slaIds?.length > 0 ? (
                <Grid
                  container
                  spacing={0}
                  className={classes.pageContainerPadding}
                >
                  <Grid item xs={12} sm={12}>
                    <SLATransactionsTable
                      onEdit={handleSLA}
                      allTransactions={allTransactions}
                    />
                  </Grid>
                </Grid>
              ) : (
                <div className={classes.pageContainerPadding}>
                  <Typography>No transactions to show.</Typography>
                </div>
              )}
            </>
          ) : (
            <EmptyConfigBox
              navigatePath={() => handleSLA('addSLA')}
              flows={true}
              isToolAdmin={true}
              text="SLA"
            />
          )}
          {popupState.open && <SLAPopup {...popupState} />}
        </>
      )}
    </>
  );
};

export default SLAPage;
